import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, ReplaySubject, map, tap } from 'rxjs';
import {
  FilterField,
  FilterGroup,
  PaginationConfig,
  SortConfig
} from '../models/filter.models';
import {
  Area,
  AreaProperties,
  Cluster,
  ClusterRequest,
  FieldKeysResponse,
  FieldValuesResponse,
  FilterBody
} from '../models/general.models';
import { FilterBuilderUtil } from '../utils/filter-builder.util';
import { BaseApiService } from './base-api.service';
import { CustomKeycloakService } from './keycloak.service';

export interface AreaFilter {
  field: string;
  operator: string;
  value: unknown;
}

export interface AreaSearchRequest {
  query: string;
  filters?: AreaFilter[];
  skip?: number;
  limit?: number;
}

// New API search request interface
export interface AreaSearchRequestNew {
  query: string;
  filters?: string;
  skip?: number;
  limit?: number;
  project_id?: string;
  folder_id?: string;
  geo_bounds?: {
    type: string;
    coordinates: number[][][];
  };
}

// New API search response interface
export interface AreaSearchResponse {
  total: number;
  hits: AreaSearchHit[];
  took: number;
  aggregations?: {
    geometry_types?: {
      buckets: Array<{ key: string; doc_count: number }>;
    };
    project_breakdown?: {
      buckets: Array<{ key: string; doc_count: number }>;
    };
    area_stats?: {
      count: number;
      min: number;
      max: number;
      avg: number;
    };
  };
}

// Area search hit with highlighting
export interface AreaSearchHit extends Area {
  _score?: number;
  _highlight?: {
    name?: string[];
    description?: string[];
    all_text?: string[];
  };
}

export interface AreaListResponse {
  items: Area[];
  total: number;
  skip: number;
  limit: number;
  // Legacy fields for backward compatibility
  features?: Area[];
  meta?: {
    count: number;
    next: string;
    previous: string;
    current_window?: {
      end: number;
      start: number;
    };
  };
}

@Injectable({
  providedIn: 'root'
})
export class AreaService extends BaseApiService {
  public areasList: ReplaySubject<Array<Area>> = new ReplaySubject<Array<Area>>(
    1
  );
  public filterBody: FilterBody = {
    sort: 'created_at',
    tags: [],
    metadata: []
  };
  public count: number = 0;

  constructor(
    http: HttpClient,
    router: Router,
    keycloakService: CustomKeycloakService
  ) {
    super(http, router, keycloakService);
  }

  private applyFilter(areas: Array<Area>): Array<Area> {
    // Removed console.log for production
    // Removed console.log for production

    let newList: Array<Area>;
    if (this.filterBody.tags && this.filterBody.tags.length) {
      newList = areas.filter(
        (area) =>
          (area.properties.groups || []).filter((group) => {
            return this.filterBody.tags.some((tag) => tag.id === group.id);
          }).length
      );
    } else {
      newList = areas;
    }

    // Removed console.log for production
    return newList;
  }

  public getAreas(
    limit: number,
    offset: number,
    projectId?: number | string,
    folderId?: number | string
  ): ReplaySubject<Array<Area>> | Observable<Array<Area>> {
    // Removed debugger statement for production
    // Use new API endpoint
    let endpoint = `${this.baseUrl}/projects/${projectId}/areas?skip=${offset}&limit=${limit}`;

    // Build filters string using new API format
    const filters: string[] = [];

    if (folderId) {
      filters.push(`folder_id:eq:${folderId}`);
    }

    if (this.filterBody.tags && this.filterBody.tags.length) {
      const tagIds = this.filterBody.tags.map((group) => group.id).join('|');
      filters.push(`properties.groups:in:${tagIds}`);
    }

    if (this.filterBody.metadata && this.filterBody.metadata.length) {
      this.filterBody.metadata.forEach((meta) => {
        const operator = this.convertOldOperatorToNew(meta.type);
        filters.push(`properties.${meta.key}:${operator}:${meta.value}`);
      });
    }

    if (filters.length > 0) {
      endpoint += `&filters=${filters.join(',')}`;
    }

    // Add sorting
    if (this.filterBody.sort) {
      const sortDirection = this.filterBody.sort.startsWith('-')
        ? 'desc'
        : 'asc';
      const sortField = this.filterBody.sort.replace(/^-/, '');
      endpoint += `&sorts=${sortField}:${sortDirection}`;
    }

    if (!this.areasList) this.areasList = new ReplaySubject();
    return this.http.get<AreaListResponse>(endpoint).pipe(
      tap((result) => {
        // Removed console.log for production
        this.count = result.total;
        // Handle both old and new API formats
        const areas = result.items || result.features || [];
        // Removed console.log for production
        this.areasList.next(this.applyFilter(areas));
      }),
      map((response) => {
        // Handle both old and new API formats
        const areas = response.items || response.features || [];
        // Removed console.log for production
        return areas;
      }),
      tap((result) => {
        // Removed console.log for production
        this.areasList.next(this.applyFilter(result));
      }),
      map((response) => {
        const filtered = this.applyFilter(response);
        // Removed console.log for production
        return filtered;
      })
    );
  }

  private convertOldOperatorToNew(oldOperator: string): string {
    const operatorMap: { [key: string]: string } = {
      __eq: 'eq',
      __ne: 'ne',
      __gt: 'gt',
      __gte: 'gte',
      __lt: 'lt',
      __lte: 'lte',
      __contains: 'contains',
      __in: 'in',
      __is_null: 'is_null',
      __is_not_null: 'is_not_null'
    };
    return operatorMap[oldOperator] || 'eq';
  }

  public getAreaByFilter(filterBody: {
    [key: string]: unknown;
  }): Observable<AreaListResponse> {
    // Extract project ID from filter body
    const projectId = filterBody['project'];
    const folderId = filterBody['folder_id'] || projectId; // Use folder_id if provided, otherwise use projectId for root level
    if (!projectId) {
      throw new Error('Project ID is required for the new API');
    }

    // Use new API endpoint
    let endpoint = `${this.baseUrl}/projects/${projectId}/areas`;

    const filters: string[] = [];

    // Add mandatory folder_id filter
    filters.push(`folder_id:eq:${folderId}`);

    Object.entries(filterBody).forEach(([key, value]) => {
      // Skip project field as it's used for the endpoint
      if (key === 'project') return;

      // Convert old filter format to new format
      if (key.includes('__')) {
        const [field, operator] = key.split('__');
        const newOperator = this.convertOldOperatorToNew(`__${operator}`);
        filters.push(`${field}:${newOperator}:${value}`);
      } else {
        filters.push(`${key}:eq:${value}`);
      }
    });

    if (filters.length > 0) {
      endpoint += `?filters=${filters.join(',')}`;
    }

    return this.http.get<AreaListResponse>(endpoint);
  }

  public getAreaPositionById(
    id: number,
    projectId?: number | string,
    folderId?: number | string
  ): Observable<AreaListResponse> {
    let endpoint = `${this.baseUrl}/projects/${projectId}/areas?cursor_id=${id}&limit=20`;

    const filters: string[] = [];

    if (folderId) {
      filters.push(`folder_id:eq:${folderId}`);
    }

    if (this.filterBody.tags && this.filterBody.tags.length) {
      const tagIds = this.filterBody.tags.map((group) => group.id).join('|');
      filters.push(`properties.groups:in:${tagIds}`);
    }

    if (filters.length > 0) {
      endpoint += `&filters=${filters.join(',')}`;
    }

    // Add sorting
    if (this.filterBody.sort) {
      const sortDirection = this.filterBody.sort.startsWith('-')
        ? 'desc'
        : 'asc';
      const sortField = this.filterBody.sort.replace(/^-/, '');
      endpoint += `&sorts=${sortField}:${sortDirection}`;
    }

    this.areasList = new ReplaySubject();
    return this.http.get<AreaListResponse>(endpoint).pipe(
      tap((result) => {
        this.count = result.total;
        // Handle both old and new API formats
        const areas = result.items || result.features || [];
        this.areasList.next(this.applyFilter(areas));
      }),
      map((response) => ({
        ...response,
        features: response.items || response.features || [],
        meta: {
          count: response.total,
          next: '',
          previous: '',
          current_window: {
            start: response.skip,
            end: response.skip + response.limit
          }
        }
      }))
    );
  }

  public clearAreaList() {
    this.areasList = new ReplaySubject<Array<Area>>(1);
  }

  public searchAreas(
    search?: string,
    projectId?: number | string
  ): Observable<Array<Area>> {
    const endpoint = `${this.baseUrl}/areas/search`;

    const searchRequest: AreaSearchRequest = {
      query: search || '',
      skip: 0,
      limit: 100
    };

    if (projectId) {
      searchRequest.filters = [
        {
          field: 'project_id',
          operator: 'eq',
          value: projectId
        }
      ];
    }

    return this.http
      .post<AreaListResponse>(endpoint, searchRequest)
      .pipe(map((response) => response.items));
  }

  public updateAreas(newList: Array<Area>) {
    this.areasList.next(this.applyFilter(newList));
  }

  public createArea(area: {
    name: string;
    project_id?: string; // Optional - if not provided, will be taken from URL
    folder_id?: string;
    tenant_id?: string;
    team_id?: string;
    geometry: {
      type: string;
      coordinates: number[] | number[][] | number[][][];
    };
    properties?: Record<string, unknown>;
  }): Observable<Area> {
    // Use new API endpoint
    // If project_id is provided, use it; otherwise, it should come from the calling context
    const projectId = area.project_id || this.getCurrentProjectId();
    const endpoint = `${this.baseUrl}/projects/${projectId}/areas`;

    // Remove project_id from request body to avoid the "project_id in request body must match project_id in URL" error
    const { project_id, ...requestBody } = area;

    return this.http.post<Area>(endpoint, requestBody);
  }

  private getCurrentProjectId(): string {
    // This should be implemented to get the current project ID from the context
    // For now, we'll throw an error if project_id is not provided
    throw new Error('project_id is required for area creation');
  }

  public uploadAreaFile(
    file: { file: File },
    projectId: string
  ): Observable<{ message: string; task_id: string }> {
    const endpoint = `${this.baseUrl}/uploads`;
    const formData: FormData = new FormData();
    formData.append('file', file.file);
    formData.append(
      'metadata',
      JSON.stringify({ project_id: projectId, type: 'area_upload' })
    );
    return this.http.post<{ message: string; task_id: string }>(
      endpoint,
      formData
    );
  }

  public deleteArea(
    projectId: string,
    id: string
  ): Observable<Record<string, unknown>> {
    const endpoint = `${this.baseUrl}/projects/${projectId}/areas/${id}`;
    return this.http.delete<Record<string, unknown>>(endpoint);
  }

  public deleteBulkAreas(
    project_id: string,
    exclude_area_ids?: string[],
    include_area_ids?: string[],
    filters?: Record<string, unknown>
  ): Observable<Record<string, unknown>> {
    let filtersString = '';

    if (include_area_ids && include_area_ids.length) {
      filtersString =
        include_area_ids.length === 1
          ? `id:eq:${include_area_ids[0]}`
          : `id:in:${include_area_ids.join('|')}`;
    } else if (filters) {
      const filterArray: string[] = [];
      Object.entries(filters).forEach(([key, value]) => {
        if (key.includes('__')) {
          const [field, operator] = key.split('__');
          const newOperator = this.convertOldOperatorToNew(`__${operator}`);
          filterArray.push(`${field}:${newOperator}:${value}`);
        } else {
          filterArray.push(`${key}:eq:${value}`);
        }
      });
      filtersString = filterArray.join(',');
    } else if (exclude_area_ids && exclude_area_ids.length) {
      filtersString = `id:not_in:${exclude_area_ids.join('|')}`;
    }

    const data: Record<string, unknown> = {
      filters: filtersString,
      dry_run: false
    };

    const endpoint = `${this.baseUrl}/projects/${project_id}/areas/bulk-delete`;
    return this.http.post<Record<string, unknown>>(endpoint, data);
  }

  public deleteBulkAreasDryRun(
    project_id: string,
    exclude_area_ids?: string[],
    include_area_ids?: string[],
    filters?: Record<string, unknown>
  ): Observable<Record<string, unknown>> {
    // Same filter construction as above, but with dry_run: true
    let filtersString = '';

    if (include_area_ids && include_area_ids.length) {
      filtersString =
        include_area_ids.length === 1
          ? `id:eq:${include_area_ids[0]}`
          : `id:in:${include_area_ids.join('|')}`;
    } else if (filters) {
      const filterArray: string[] = [];
      Object.entries(filters).forEach(([key, value]) => {
        if (key.includes('__')) {
          const [field, operator] = key.split('__');
          const newOperator = this.convertOldOperatorToNew(`__${operator}`);
          filterArray.push(`${field}:${newOperator}:${value}`);
        } else {
          filterArray.push(`${key}:eq:${value}`);
        }
      });
      filtersString = filterArray.join(',');
    } else if (exclude_area_ids && exclude_area_ids.length) {
      filtersString = `id:not_in:${exclude_area_ids.join('|')}`;
    }

    const endpoint = `${this.baseUrl}/projects/${project_id}/areas/bulk-delete`;
    const data: Record<string, unknown> = {
      filters: filtersString,
      dry_run: true
    };

    return this.http.post<Record<string, unknown>>(endpoint, data);
  }

  public changeArea(
    projectId: string,
    area: Area,
    body: Partial<AreaProperties>
  ): Observable<Area> {
    // Use the new API endpoint format: PATCH /projects/{project_id}/areas/{area_id}
    const areaId = area.id || area.properties?.id;
    const endpoint = `${this.baseUrl}/projects/${projectId}/areas/${areaId}`;

    // Transform the body to match the new API schema
    // The new API expects fields directly in the request body, not nested in properties
    const requestBody: Record<string, unknown> = {};

    if ((body as any).name) {
      (requestBody as any).name = (body as any).name;
    }
    if ((body as any)['description']) {
      (requestBody as any).description = (body as any)['description'];
    }
    if ((body as any)['geometry']) {
      (requestBody as any).geometry = (body as any)['geometry'];
    }
    if ((body as any).properties) {
      (requestBody as any).properties = (body as any).properties;
    }
    if ((body as any)['area_metadata']) {
      (requestBody as any).area_metadata = (body as any)['area_metadata'];
    }
    if ((body as any).groups) {
      (requestBody as any).properties = {
        ...((requestBody as any).properties || {}),
        groups: (body as any).groups
      };
    }

    return this.http.patch<Area>(endpoint, requestBody);
  }

  public getShared(area: Area): Observable<Area> {
    const endpoint = `${this.baseUrl}/shares/area/${area.properties.id}`;
    return this.http.get<Area>(endpoint);
  }

  public getAreasByBbox(
    bbox: number[] | null,
    project_id: string,
    folder_id?: string
  ): Observable<Array<Area>> {
    // Use new API endpoint
    let endpoint = `${this.baseUrl}/projects/${project_id}/areas`;

    const filters: AreaFilter[] = [
      {
        field: 'folder_id',
        operator: 'eq',
        value: folder_id || project_id
      }
    ];

    if (this.filterBody.tags && this.filterBody.tags.length) {
      filters.push({
        field: 'properties.groups',
        operator: 'in',
        value: this.filterBody.tags.map((group) => group.id).join('|')
      });
    }

    if (this.filterBody.metadata && this.filterBody.metadata.length) {
      this.filterBody.metadata.forEach((meta) => {
        const operator = this.convertOldOperatorToNew(meta.type);
        filters.push({
          field: `properties.${meta.key}`,
          operator,
          value: meta.value
        });
      });
    }

    const searchRequest: AreaSearchRequest = {
      query: '',
      filters,
      skip: 0,
      limit: 1000
    };

    if (searchRequest.filters.length > 0) {
      endpoint += `?filters=${searchRequest.filters
        .map((filter) => Object.values(filter).join(':'))
        .join(',')}`;
    }
    // Skip bbox parameter entirely
    if (searchRequest.limit) {
      endpoint += `${searchRequest.filters.length ? '&' : '?'}limit=${
        searchRequest.limit
      }`;
    }
    if (searchRequest.skip || searchRequest.skip === 0) {
      endpoint += `${
        searchRequest.filters.length || searchRequest.limit ? '&' : '?'
      }skip=${searchRequest.skip}`;
    }

    // Add cache-busting parameter to ensure fresh data
    endpoint += `${
      searchRequest.filters.length ||
      searchRequest.limit ||
      searchRequest.skip !== undefined
        ? '&'
        : '?'
    }t=${Date.now()}`;

    return this.http.get<AreaListResponse>(endpoint).pipe(
      map((response) => {
        // Handle both old and new API formats for map component
        // Removed console.log for production
        return response.items || response.features || [];
      })
    );
  }

  /**
   * Get clustered areas for map visualization
   * POST /projects/{project_id}/areas/cluster
   */
  public getClusters(
    projectId: string,
    zoom: number,
    bounds: { west: number; south: number; east: number; north: number },
    folderId?: string
  ): Observable<Cluster> {
    const endpoint = `${this.baseUrl}/projects/${projectId}/areas/cluster`;

    const zoomInt = Math.round(zoom);

    const requestBody: ClusterRequest = {
      zoom: zoomInt,
      bounds: {
        west: bounds.west,
        south: bounds.south,
        east: bounds.east,
        north: bounds.north
      }
    };

    if (folderId) {
      requestBody.filters = `folder_id:eq:${folderId}`;
    }

    return this.http.post<Cluster>(endpoint, requestBody);
  }

  public getAreaGeoJSON(
    projectId: string,
    id: string
  ): Observable<Record<string, unknown>> {
    const endpoint = `${this.baseUrl}/projects/${projectId}/areas/${id}/geojson`;
    return this.http.get<Record<string, unknown>>(endpoint);
  }

  public getAreaMetadataKeys(projectId?: string): Observable<string[]> {
    const endpoint = `${this.baseUrl}/projects/${projectId}/areas/metadata-keys`;

    return this.http.get<string[]>(endpoint);
  }

  public getAreaMetadataValues(
    key: string,
    projectId?: string
  ): Observable<string[]> {
    const endpoint = `${this.baseUrl}/projects/${projectId}/areas/metadata-values/${key}`;

    return this.http.get<string[]>(endpoint);
  }

  // ===== NEW API GET METHODS =====

  /**
   * Get areas in a specific folder using new API
   * @param projectId - The project ID
   * @param folderId - The folder ID (mandatory for new API)
   * @param filters - Optional filters using new filter system
   * @param pagination - Optional pagination config
   * @param sorts - Optional sort configs
   */
  public getAreasInFolder(
    projectId: string,
    folderId: string,
    filters?: FilterField[] | FilterGroup,
    pagination?: PaginationConfig,
    sorts?: SortConfig[]
  ): Observable<AreaListResponse> {
    let endpoint = `${this.baseUrl}/projects/${projectId}/areas`;

    // Build query parameters
    const params: string[] = [];

    // MANDATORY: folder_id filter (critical for new API)
    params.push(`filters=folder_id:eq:${folderId}`);

    // Add sorting
    if (sorts && sorts.length > 0) {
      const sortString = FilterBuilderUtil.toSortString(sorts);
      params.push(`sort=${encodeURIComponent(sortString)}`);
    }

    // Add pagination
    if (pagination) {
      if (pagination.skip !== undefined) {
        params.push(`skip=${pagination.skip}`);
      }
      if (pagination.limit !== undefined) {
        params.push(`limit=${pagination.limit}`);
      }
      if (pagination.cursor) {
        params.push(`cursor_id=${pagination.cursor}`);
      }
    }

    if (params.length > 0) {
      endpoint += `?${params.join('&')}`;
    }

    // Add cache-busting parameter to ensure fresh data
    endpoint += `${params.length > 0 ? '&' : '?'}t=${Date.now()}`;

    return this.http.get<AreaListResponse>(endpoint);
  }

  /**
   * Get a single area by ID using new API
   * @param projectId - The project ID
   * @param areaId - The area ID
   */
  public getAreaById(projectId: string, areaId: string): Observable<Area> {
    const endpoint = `${this.baseUrl}/projects/${projectId}/areas/${areaId}`;
    return this.http.get<Area>(endpoint);
  }

  /**
   * Get areas with cursor pagination for detail view navigation
   * @param projectId - The project ID
   * @param folderId - The folder ID
   * @param cursorId - The area ID to center on
   * @param beforeCount - Number of items before cursor
   * @param afterCount - Number of items after cursor
   */
  public getAreasWithCursor(
    projectId: string,
    folderId: string,
    cursorId: string,
    beforeCount: number = 10,
    afterCount: number = 10
  ): Observable<AreaListResponse> {
    const endpoint = `${this.baseUrl}/projects/${projectId}/areas?filters=folder_id:eq:${folderId}&cursor_id=${cursorId}&before_count=${beforeCount}&after_count=${afterCount}`;
    return this.http.get<AreaListResponse>(endpoint);
  }

  /**
   * Get areas with geographic bounds filtering
   * @param projectId - The project ID
   * @param folderId - The folder ID
   * @param bbox - Bounding box [minLng, minLat, maxLng, maxLat]
   * @param additionalFilters - Optional additional filters
   */
  public getAreasInBounds(
    projectId: string,
    folderId: string,
    bbox: number[],
    additionalFilters?: FilterField[]
  ): Observable<AreaListResponse> {
    let endpoint = `${this.baseUrl}/projects/${projectId}/areas`;

    const params: string[] = [];

    // MANDATORY: folder_id filter
    params.push(`filters=folder_id:eq:${folderId}`);

    // Add additional filters
    if (additionalFilters && additionalFilters.length > 0) {
      const filterString = FilterBuilderUtil.toFilterString(additionalFilters);
      params.push(`filters=${encodeURIComponent(filterString)}`);
    }

    // Add bounding box
    if (bbox && bbox.length === 4) {
      params.push(`bbox=${bbox.join(',')}`);
    }

    if (params.length > 0) {
      endpoint += `?${params.join('&')}`;
    }

    return this.http.get<AreaListResponse>(endpoint);
  }

  /**
   * Search areas using new search API
   * @param query - Search query string
   * @param projectId - Optional project ID to limit search
   * @param folderId - Optional folder ID to limit search
   * @param filters - Optional filters
   * @param pagination - Optional pagination
   */
  public searchAreasNew(
    query: string,
    projectId?: string,
    folderId?: string,
    filters?: FilterField[] | FilterGroup,
    pagination?: PaginationConfig
  ): Observable<AreaSearchResponse> {
    const endpoint = `${this.baseUrl}/areas/search`;

    const searchRequest: AreaSearchRequestNew = {
      query,
      skip: pagination?.skip || 0,
      limit: pagination?.limit || 100
    };

    // Add filters if provided
    if (filters) {
      if (Array.isArray(filters)) {
        searchRequest.filters = FilterBuilderUtil.toFilterString(filters);
      } else {
        searchRequest.filters = FilterBuilderUtil.filterGroupToString(filters);
      }
    }

    // Add project/folder scoping
    if (projectId) {
      searchRequest.project_id = projectId;
    }
    if (folderId) {
      searchRequest.folder_id = folderId;
    }

    return this.http.post<AreaSearchResponse>(endpoint, searchRequest);
  }

  /**
   * Get areas with advanced filtering using the new filter system
   * @param projectId - The project ID
   * @param folderId - The folder ID
   * @param filterConfig - Complete filter configuration
   */
  public getAreasWithAdvancedFilters(
    projectId: string,
    folderId: string,
    filterConfig: {
      filters?: FilterField[] | FilterGroup;
      sorts?: SortConfig[];
      pagination?: PaginationConfig;
    }
  ): Observable<AreaListResponse> {
    let endpoint = `${this.baseUrl}/projects/${projectId}/areas`;

    const params: string[] = [];

    // Build filter string - always include folder_id, then add additional filters
    const filterParts: string[] = [`folder_id:eq:${folderId}`];

    // Add additional filters
    if (filterConfig.filters) {
      let additionalFilterString = '';
      if (Array.isArray(filterConfig.filters)) {
        additionalFilterString = FilterBuilderUtil.toFilterString(filterConfig.filters);
      } else {
        additionalFilterString = FilterBuilderUtil.filterGroupToString(
          filterConfig.filters
        );
      }
      if (additionalFilterString) {
        filterParts.push(additionalFilterString);
      }
    }

    // Combine all filters into single parameter
    if (filterParts.length > 0) {
      const combinedFilters = filterParts.join(',');
      params.push(`filters=${encodeURIComponent(combinedFilters)}`);
    }

    // Add sorting
    if (filterConfig.sorts && filterConfig.sorts.length > 0) {
      const sortString = FilterBuilderUtil.toSortString(filterConfig.sorts);
      params.push(`sort=${encodeURIComponent(sortString)}`);
    }

    // Add pagination
    if (filterConfig.pagination) {
      if (filterConfig.pagination.skip !== undefined) {
        params.push(`skip=${filterConfig.pagination.skip}`);
      }
      if (filterConfig.pagination.limit !== undefined) {
        params.push(`limit=${filterConfig.pagination.limit}`);
      }
      if (filterConfig.pagination.cursor) {
        params.push(`cursor_id=${filterConfig.pagination.cursor}`);
      }
    }

    if (params.length > 0) {
      endpoint += `?${params.join('&')}`;
    }

    return this.http.get<AreaListResponse>(endpoint);
  }

  /**
   * Get available filter fields for a project
   * @param projectId - The project ID
   * @param folderId - Optional folder ID to scope fields to a specific folder
   */
  public getFieldKeys(
    projectId: string,
    folderId?: string
  ): Observable<FieldKeysResponse> {
    let endpoint = `${this.baseUrl}/projects/${projectId}/areas/field-keys`;
    if (folderId) {
      endpoint += `?folder_id=${folderId}`;
    }
    return this.http.get<FieldKeysResponse>(endpoint);
  }

  /**
   * Get unique values for a specific field
   * @param projectId - The project ID
   * @param fieldName - The field name (e.g., "name", "properties.status", "area_metadata.year")
   * @param folderId - Optional folder ID to scope values to a specific folder
   * @param limit - Number of values to return (default 10, max 100)
   * @param offset - Number of values to skip (default 0)
   */
  public getFieldValues(
    projectId: string,
    fieldName: string,
    folderId?: string,
    limit: number = 10,
    offset: number = 0
  ): Observable<FieldValuesResponse> {
    let endpoint = `${this.baseUrl}/projects/${projectId}/areas/field-values`;
    const params: string[] = [`field_name=${encodeURIComponent(fieldName)}`];

    if (folderId) {
      params.push(`folder_id=${folderId}`);
    }
    if (limit) {
      params.push(`limit=${limit}`);
    }
    if (offset) {
      params.push(`offset=${offset}`);
    }

    endpoint += `?${params.join('&')}`;
    return this.http.get<FieldValuesResponse>(endpoint);
  }

  /**
   * Move areas to a different folder
   * @param projectId - The project ID
   * @param filters - Filter string to select areas (e.g., "id:in:uuid1|uuid2" or "id:eq:uuid")
   * @param newFolderId - The destination folder ID
   * @param dryRun - If true, preview what would be moved without actually moving
   */
  public moveAreas(
    projectId: string,
    filters: string,
    newFolderId: string,
    dryRun: boolean = false
  ): Observable<{
    moved_count: number;
    moved_ids: string[];
    dry_run: boolean;
    message: string;
  }> {
    const endpoint = `${this.baseUrl}/projects/${projectId}/areas/move`;
    return this.http.post<{
      moved_count: number;
      moved_ids: string[];
      dry_run: boolean;
      message: string;
    }>(endpoint, {
      filters,
      new_folder_id: newFolderId,
      dry_run: dryRun
    });
  }
}
